import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  HelpCircle,
  Send,
  Loader2,
  MessageSquare,
  CheckCircle2,
  ListChecks,
  RefreshCw,
  Trash2,
  ImageIcon,
  Sparkles,
} from "lucide-react";
import { linkifyText } from "../utils/linkifyText";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  { value: "tecnico", label: "Problema tecnico" },
  { value: "fatturazione", label: "Fatturazione e pagamenti" },
  { value: "funzionalita", label: "Richiesta nuova funzionalità" },
  { value: "alloggiati", label: "Alloggiati Web" },
  { value: "altro", label: "Altro" },
];

const CATEGORIE_LABEL = Object.fromEntries(CATEGORIE.map((c) => [c.value, c.label]));

const STATO_LABEL = {
  nuova: "Nuova",
  "in lavorazione": "In lavorazione",
  lavorata: "Lavorata",
};

const STATO_BADGE = {
  nuova: "bg-sky-100 text-sky-800 border-sky-200",
  "in lavorazione": "bg-amber-100 text-amber-800 border-amber-200",
  lavorata: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch {
    return String(iso);
  }
}

function AssistenzaUserImage({ path, token, alt = "Allegato", className = "" }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!path || !token) return undefined;
    let objectUrl;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, token]);

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className}`}>
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} />;
}

export default function Assistenza() {
  const { token, user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [loadingRichieste, setLoadingRichieste] = useState(true);
  const [richieste, setRichieste] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    categoria: "",
    oggetto: "",
    messaggio: "",
    telefono: "",
  });

  const loadRichieste = async ({ markRead = false } = {}) => {
    setLoadingRichieste(true);
    try {
      const res = await axios.get(`${API_URL}/api/assistenza/richieste`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = res.data?.richieste || [];
      setRichieste(items);

      if (markRead && items.some((r) => r.unread_for_user)) {
        await axios.post(
          `${API_URL}/api/assistenza/richieste/mark-read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRichieste((prev) => prev.map((r) => ({ ...r, unread_for_user: false })));
        window.dispatchEvent(new CustomEvent("assistenza-unread-changed", { detail: { count: 0 } }));
      }
    } catch (error) {
      console.error("Errore caricamento richieste:", error);
    } finally {
      setLoadingRichieste(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadRichieste({ markRead: true });
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.categoria || !formData.messaggio) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/assistenza/richiesta`,
        {
          ...formData,
          user_email: user?.email,
          user_name: user?.name,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSent(true);
      setFormData({ categoria: "", oggetto: "", messaggio: "", telefono: "" });
      toast.success("Richiesta inviata con successo!");
      await loadRichieste();
    } catch (error) {
      console.error("Errore invio richiesta:", error);
      toast.error("Errore nell'invio della richiesta");
    } finally {
      setSending(false);
    }
  };

  const eliminaRichiesta = async (id) => {
    if (!window.confirm("Eliminare questa richiesta dal tuo elenco?")) return;
    setDeletingId(id);
    try {
      await axios.post(`${API_URL}/api/assistenza/richieste/${id}/elimina`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Richiesta eliminata");
      await loadRichieste();
    } catch (error) {
      console.error("Errore eliminazione richiesta:", error);
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  const openAllegato = async (url) => {
    try {
      const res = await fetch(`${API_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Impossibile aprire la foto");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto">
      <PageHeader
        title="Assistenza"
        subtitle="Hai bisogno di aiuto? Siamo qui per te."
        icon={HelpCircle}
        iconContainerClassName="bg-[#3B5998]"
        iconClassName="text-white"
      />

      {sent ? (
        <Card className="card-base mb-6 border-emerald-200 bg-emerald-50/30">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-800">Richiesta inviata!</p>
              <p className="text-sm text-slate-600">
                Il team ti risponderà al più presto. Puoi seguire lo stato nella sezione qui sotto.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="card-base mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="w-5 h-5 text-[#3B5998]" />
            Invia una Richiesta
          </CardTitle>
          <CardDescription>
            Descrivi il tuo problema o la tua domanda e ti risponderemo al più presto
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Categoria *</Label>
              <Select
                value={formData.categoria}
                onValueChange={(value) => setFormData({ ...formData, categoria: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona una categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIE.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium">Oggetto</Label>
              <Input
                value={formData.oggetto}
                onChange={(e) => setFormData({ ...formData, oggetto: e.target.value })}
                placeholder="Breve descrizione del problema"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Messaggio *</Label>
              <Textarea
                value={formData.messaggio}
                onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                placeholder="Descrivi nel dettaglio il problema o la tua richiesta..."
                className="mt-1 min-h-[150px]"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Telefono (opzionale)</Label>
              <Input
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="Per essere ricontattato telefonicamente"
                className="mt-1"
              />
            </div>

            <Button
              type="submit"
              disabled={sending}
              className="w-full h-12 bg-[#3B5998] hover:bg-[#2d4373] text-white"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              Invia Richiesta
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="card-base mb-6">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ListChecks className="w-5 h-5 text-[#3B5998]" />
              Le tue richieste
            </CardTitle>
            <CardDescription>
              Storico delle richieste, stato di lavorazione e risposte del team
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadRichieste({ markRead: true })} disabled={loadingRichieste}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingRichieste ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </CardHeader>
        <CardContent>
          {loadingRichieste ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[#3B5998]" />
            </div>
          ) : richieste.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              Non hai ancora inviato richieste di assistenza.
            </p>
          ) : (
            <div className="space-y-3">
              {richieste.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-lg border p-4 transition-colors ${
                    r.unread_for_user
                      ? "border-teal-300 bg-teal-50/50 shadow-[0_0_0_1px_rgba(13,148,136,0.12)]"
                      : "border-slate-200 bg-slate-50/50"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900">
                          {CATEGORIE_LABEL[r.categoria] || r.categoria}
                          {r.oggetto ? ` — ${r.oggetto}` : ""}
                        </p>
                        {r.unread_for_user ? (
                          <Badge className="bg-teal-600 text-white border-0 text-[10px] gap-1">
                            <Sparkles className="w-3 h-3" />
                            Aggiornamento
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-slate-500">{formatWhen(r.created_at)}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className={STATO_BADGE[r.stato] || "bg-slate-100 text-slate-700"}
                    >
                      {STATO_LABEL[r.stato] || r.stato}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.messaggio}</p>
                  {r.stato_updated_at && r.stato !== "nuova" ? (
                    <p className="text-xs text-slate-400 mt-2">
                      Stato aggiornato: {formatWhen(r.stato_updated_at)}
                    </p>
                  ) : null}

                  {(r.risposte || []).length > 0 ? (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">
                        Risposte del team
                      </p>
                      {(r.risposte || []).map((risposta) => (
                        <div
                          key={risposta.id}
                          className="rounded-md border border-teal-100 bg-white p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                            <p className="text-xs font-medium text-teal-800">
                              {risposta.admin_name || "Assistenza Affidya"}
                            </p>
                            <p className="text-[11px] text-slate-400">
                              {formatWhen(risposta.created_at)}
                            </p>
                          </div>
                          {risposta.messaggio ? (
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {linkifyText(risposta.messaggio)}
                            </p>
                          ) : null}
                          {(risposta.allegati || []).length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {risposta.allegati.map((a) => (
                                <button
                                  key={a.id}
                                  type="button"
                                  onClick={() => openAllegato(a.url)}
                                  className="block rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-teal-500"
                                >
                                  <AssistenzaUserImage
                                    path={a.url}
                                    token={token}
                                    alt={a.nome || "Foto"}
                                    className="h-20 w-20 object-cover border border-slate-200"
                                  />
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={deletingId === r.id}
                      onClick={() => eliminaRichiesta(r.id)}
                    >
                      {deletingId === r.id ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                      )}
                      Elimina
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
